import React, { useState, useEffect } from 'react';
import {
  Car,
  Calendar,
  IndianRupee,
  Route,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  ShieldAlert,
  XCircle,
  Download,
  Maximize2,
  RefreshCw,
  BarChart3,
  LineChart as LineChartIcon,
  AreaChart as AreaChartIcon,
  PieChart,
  Users,
  Building2,
  Search,
  ChevronLeft,
  ChevronRight,
  Share2,
  Copy,
  ExternalLink,
  MessageCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import PageHeader from '../components/layout/PageHeader';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import Button from '../components/ui/Button';
import CarLoader from '../components/ui/CarLoader';
import { formatCurrency, formatDateTime, formatName, formatVehicleName } from '../lib/utils';
import { carAPI, bookingAPI, journeyAPI, reportsAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    yellow: "bg-yellow-100 text-yellow-600",
    purple: "bg-purple-100 text-purple-600"
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-600">{title}</p>
            <h3 className="text-xl font-bold text-gray-900 mt-1">{value}</h3>
            {trend && (
              <div className="flex items-center mt-1">
                {trend === 'up' ? (
                  <ArrowUpRight className="h-3 w-3 text-green-600" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 text-red-600" />
                )}
                <span className={`text-xs ml-0.5 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                  {trendValue}
                </span>
                <span className="text-xs text-gray-500 ml-1">vs last month</span>
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Share card so owners can send their public travels page to customers
const ShareTravelsCard = ({ ownerId, travelsName }) => {
  const [copied, setCopied] = useState(false);
  // Share the backend /share URL: it serves a rich link preview (cover, logo, name)
  // for WhatsApp/Facebook, then redirects real visitors to the public page.
  const apiOrigin = (process.env.REACT_APP_API_URL || `${window.location.origin}/api`).replace(/\/api\/?$/, '');
  const shareUrl = `${apiOrigin}/share/travels/${ownerId}`;
  const shareText = `Check out ${travelsName || 'our travels'} — view our vehicles & packages and book directly:`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: travelsName || 'Our Travels', text: shareText, url: shareUrl }).catch(() => {});
    } else {
      handleCopy();
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <Card className="border-blue-100 bg-gradient-to-br from-blue-50 to-white">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl flex-shrink-0">
              <Share2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Share Your Page</h3>
              <p className="text-xs text-gray-500 mt-0.5">Send this public link to customers — they can see your vehicles, packages & contact you directly.</p>
            </div>
          </div>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              onClick={handleNativeShare}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg flex-shrink-0"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
          )}
        </div>

        {/* Link + copy */}
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 flex items-center min-w-0 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <span className="text-xs text-gray-600 truncate">{shareUrl}</span>
          </div>
          <button
            onClick={handleCopy}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg flex-shrink-0 transition-colors ${
              copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {copied ? <><CheckCircle className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy Link</>}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center p-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg flex-shrink-0"
            title="Open page"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        {/* Social buttons */}
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1877F2] hover:opacity-90 text-white text-xs font-medium rounded-lg">
            <Share2 className="h-4 w-4" /> Facebook
          </a>
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black hover:opacity-90 text-white text-xs font-medium rounded-lg">
            <Share2 className="h-4 w-4" /> X
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalCars: 0,
    activeBookings: 0,
    totalRevenue: 0,
    activeJourneys: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [driverJourneys, setDriverJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [trends, setTrends] = useState({ cars: null, bookings: null, revenue: null, journeys: null });
  const [adminData, setAdminData] = useState({ grandTotals: {}, owners: [] });
  const [dashboardAlerts, setDashboardAlerts] = useState(null);
  const [notifyModal, setNotifyModal] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ subject: '', message: '', sendToAll: true, ownerIds: [] });
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminPage, setAdminPage] = useState(1);
  const adminPerPage = 10;

  // Chart options state
  const [chartPeriod, setChartPeriod] = useState('monthly'); // daily, weekly, monthly, yearly
  const [chartType, setChartType] = useState('area'); // line, bar, area
  const [showComparison, setShowComparison] = useState(false);

  // Send notification to owners
  const handleSendNotification = async () => {
    if (!notifyForm.subject.trim() || !notifyForm.message.trim()) return;
    setNotifySending(true);
    setNotifyResult(null);
    try {
      const res = await userAPI.notifyOwners(notifyForm);
      setNotifyResult({ type: 'success', text: res.data.message });
      setNotifyForm({ subject: '', message: '', sendToAll: true, ownerIds: [] });
    } catch (err) {
      setNotifyResult({ type: 'error', text: err.response?.data?.message || 'Failed to send' });
    } finally {
      setNotifySending(false);
    }
  };

  // Check if user is admin/owner level
  const isAdminLevel = ['owner', 'admin', 'manager', 'accountant'].includes(user?.role);
  const isDriver = user?.role === 'driver';
  const isSuperAdmin = user?.role === 'admin';

  // Calculate growth percentage from revenue data
  const totalRevenueChart = revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const totalPrevious = revenueData.reduce((sum, item) => sum + (item.previous || 0), 0);
  const growthPercentage = totalPrevious > 0 ? ((totalRevenueChart - totalPrevious) / totalPrevious * 100).toFixed(1) : 0;

  // Map frontend period names to backend groupBy values
  const periodToGroupBy = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' };

  const fetchRevenueData = async (period) => {
    try {
      setRevenueLoading(true);
      const groupBy = periodToGroupBy[period] || 'month';
      const now = new Date();
      let startDate, endDate, prevStartDate, prevEndDate;

      if (period === 'daily') {
        // Last 7 days
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        endDate = now;
        prevStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13);
        prevEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      } else if (period === 'weekly') {
        // Last 4 weeks
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 27);
        endDate = now;
        prevStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 55);
        prevEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 28);
      } else if (period === 'yearly') {
        // Last 4 years
        startDate = new Date(now.getFullYear() - 3, 0, 1);
        endDate = now;
        prevStartDate = new Date(now.getFullYear() - 7, 0, 1);
        prevEndDate = new Date(now.getFullYear() - 4, 0, 1);
      } else {
        // Monthly - current year
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = now;
        prevStartDate = new Date(now.getFullYear() - 1, 0, 1);
        prevEndDate = new Date(now.getFullYear() - 1, 11, 31);
      }

      const formatDate = (d) => d.toISOString().split('T')[0];

      const [currentRes, prevRes] = await Promise.all([
        reportsAPI.getRevenueReport({
          groupBy,
          startDate: formatDate(startDate),
          endDate: formatDate(endDate)
        }),
        reportsAPI.getRevenueReport({
          groupBy,
          startDate: formatDate(prevStartDate),
          endDate: formatDate(prevEndDate)
        }).catch(() => ({ data: { data: [] } }))
      ]);

      const currentData = currentRes.data.data || [];
      const prevData = prevRes.data?.data || [];

      // Build lookup for previous period data
      const prevLookup = {};
      prevData.forEach(item => {
        prevLookup[item._id] = item.totalRevenue || 0;
      });

      // Format labels based on period
      const formatLabel = (id, index) => {
        if (period === 'daily') {
          const d = new Date(id);
          return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] || id;
        } else if (period === 'weekly') {
          return `Week ${index + 1}`;
        } else if (period === 'monthly') {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          // _id format could be "2026-01" or "2026-1"
          const parts = id.split('-');
          const monthIdx = parseInt(parts[1], 10) - 1;
          return months[monthIdx] || id;
        } else if (period === 'yearly') {
          return id;
        }
        return id;
      };

      // Map to chart format
      const chartData = currentData.map((item, index) => ({
        label: formatLabel(item._id, index),
        revenue: item.totalRevenue || 0,
        previous: prevData[index]?.totalRevenue || 0
      }));

      // If no data from API, show empty state
      if (chartData.length === 0) {
        setRevenueData([{ label: 'No data', revenue: 0, previous: 0 }]);
      } else {
        setRevenueData(chartData);
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      setRevenueData([]);
    } finally {
      setRevenueLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user?.role]);

  // Re-fetch revenue data when chart period changes
  useEffect(() => {
    if (isAdminLevel && !loading) {
      fetchRevenueData(chartPeriod);
    }
  }, [chartPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      if (isDriver) {
        // For drivers, fetch their assigned bookings
        try {
          const [assignmentsRes, journeysRes] = await Promise.all([
            bookingAPI.getMyAssignments(),
            journeyAPI.getMyJourneys().catch(() => ({ data: { journeys: [] } }))
          ]);

          const assignments = assignmentsRes.data.bookings || assignmentsRes.data || [];
          const journeys = journeysRes.data.journeys || journeysRes.data || [];

          // Combine assignments and journeys for display
          setDriverJourneys(assignments.length > 0 ? assignments : journeys);

          // Calculate driver stats
          const activeAssignments = assignments.filter(a =>
            a.status === 'pending' || a.status === 'confirmed' || a.status === 'active'
          ).length;
          const completedAssignments = assignments.filter(a => a.status === 'completed').length;

          setStats({
            totalCars: 0,
            activeBookings: activeAssignments,
            totalRevenue: 0,
            activeJourneys: completedAssignments
          });
        } catch (error) {
          console.error('Error fetching driver assignments:', error);
          setDriverJourneys([]);
        }
      } else if (isSuperAdmin) {
        // For platform admin - fetch all owners' stats
        try {
          const res = await userAPI.getOwnersStats();
          setAdminData({
            grandTotals: res.data.grandTotals || {},
            owners: res.data.owners || [],
            revenueChart: res.data.revenueChart || [],
            expiringDocs: res.data.expiringDocs || []
          });
        } catch (error) {
          console.error('Error fetching admin stats:', error);
        }
      } else {
        // For owner level users
        const [carsRes, bookingsRes, summaryRes, alertsRes] = await Promise.all([
          carAPI.getAllCars(),
          bookingAPI.getAllBookings(),
          reportsAPI.getDashboardSummary().catch(() => null),
          reportsAPI.getDashboardAlerts().catch(() => null)
        ]);

        if (alertsRes?.data?.alerts) {
          setDashboardAlerts(alertsRes.data.alerts);
        }

        const cars = carsRes.data.cars || carsRes.data || [];
        const bookings = bookingsRes.data.bookings || bookingsRes.data || [];
        const summary = summaryRes?.data?.summary;

        // Calculate stats from real data
        const activeBookings = bookings.filter(b =>
          b.status === 'confirmed' || b.status === 'active'
        ).length;

        const totalRevenue = bookings
          .filter(b => b.status === 'completed')
          .reduce((sum, b) => sum + (b.totalCost || 0), 0);

        const activeJourneys = bookings.filter(b => b.status === 'active').length;

        setStats({
          totalCars: cars.length,
          activeBookings,
          totalRevenue: summary?.totalRevenue || totalRevenue,
          activeJourneys
        });

        // Calculate real trends by comparing current month vs previous month
        const now = new Date();
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

        const thisMonthBookings = bookings.filter(b => new Date(b.createdAt) >= thisMonthStart);
        const lastMonthBookings = bookings.filter(b => {
          const d = new Date(b.createdAt);
          return d >= lastMonthStart && d <= lastMonthEnd;
        });

        const thisMonthRevenue = thisMonthBookings
          .filter(b => b.status === 'completed')
          .reduce((sum, b) => sum + (b.totalCost || 0), 0);
        const lastMonthRevenue = lastMonthBookings
          .filter(b => b.status === 'completed')
          .reduce((sum, b) => sum + (b.totalCost || 0), 0);

        const thisMonthActive = thisMonthBookings.filter(b => b.status === 'confirmed' || b.status === 'active').length;
        const lastMonthActive = lastMonthBookings.filter(b => b.status === 'confirmed' || b.status === 'active').length;

        const calcTrend = (current, previous) => {
          if (previous === 0 && current === 0) return null;
          if (previous === 0) return { direction: 'up', value: '+100%' };
          const pct = Math.round(((current - previous) / previous) * 100);
          return {
            direction: pct >= 0 ? 'up' : 'down',
            value: `${pct >= 0 ? '+' : ''}${pct}%`
          };
        };

        setTrends({
          cars: null, // Cars don't have a meaningful monthly trend
          bookings: calcTrend(thisMonthActive, lastMonthActive),
          revenue: calcTrend(thisMonthRevenue, lastMonthRevenue),
          journeys: calcTrend(
            thisMonthBookings.filter(b => b.status === 'active').length,
            lastMonthBookings.filter(b => b.status === 'active').length
          )
        });

        // Get recent bookings (last 5)
        setRecentBookings(bookings.slice(0, 5));

        // Fetch revenue chart data
        fetchRevenueData(chartPeriod);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending' },
      confirmed: { variant: 'info', label: 'Confirmed' },
      active: { variant: 'success', label: 'Active' },
      completed: { variant: 'secondary', label: 'Completed' },
      cancelled: { variant: 'danger', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Driver Dashboard
  if (isDriver) {
    return (
      <div className="p-3 md:p-4">
        {/* Header */}
        <div className="mb-3">
          <h1 className="text-base font-bold text-gray-900">My Dashboard</h1>
          <p className="text-xs text-gray-600">Welcome back, {formatName(user?.name) || 'Driver'}! Here are your assigned journeys.</p>
        </div>

        {/* Driver Stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <StatCard
            title="Active Journeys"
            value={stats.activeBookings}
            icon={Route}
            color="blue"
          />
          <StatCard
            title="Completed Trips"
            value={stats.activeJourneys}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Total Journeys"
            value={driverJourneys.length}
            icon={MapPin}
            color="purple"
          />
        </div>

        {/* Quick Actions for Driver */}
        <Card className="mb-3">
          <CardContent className="p-3">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Quick Actions</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => navigate('/journeys')}
              >
                <Route className="h-4 w-4 mr-1" />
                View My Trips
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* My Assigned Trips */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-700">My Assigned Trips</h3>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6 px-2"
                onClick={() => navigate('/journeys')}
              >
                View All
              </Button>
            </div>
            {loading ? (
              <CarLoader size="small" message="Loading data..." />
            ) : driverJourneys.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">ID</TableHead>
                    <TableHead className="text-xs">Vehicle</TableHead>
                    <TableHead className="text-xs">Customer</TableHead>
                    <TableHead className="text-xs">Pickup</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {driverJourneys.slice(0, 5).map((item) => {
                    const pickupAddr = typeof item.pickupLocation === 'object' && item.pickupLocation !== null
                      ? (item.pickupLocation.address || item.pickupLocation.city || 'N/A')
                      : (item.pickupLocation || item.fromLocation?.address || item.startLocation || 'N/A');
                    const isJourney = !!item.fromLocation;
                    const detailPath = isJourney
                      ? `/journeys/${item._id}`
                      : (item.status === 'active' || item.status === 'confirmed' || item.status === 'pending')
                        ? `/driver/active-trip/booking/${item._id}`
                        : `/journeys`;
                    return (
                    <TableRow key={item._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(detailPath)}>
                      <TableCell className="text-xs font-medium text-blue-600">
                        #{item._id.slice(-6)}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">{formatVehicleName(item.carId, { showYear: false })}</div>
                        <div className="text-xs text-gray-500">{item.carId?.registrationNumber}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">{item.customerId?.name || item.customerName || 'N/A'}</div>
                        <div className="text-xs text-blue-600">{item.customerId?.phone || item.customerPhone}</div>
                      </TableCell>
                      <TableCell className="text-xs max-w-[150px] truncate" title={pickupAddr}>
                        {pickupAddr}
                      </TableCell>
                      <TableCell className="text-xs">{formatDateTime(item.startDate || item.createdAt)}</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-4 text-xs text-gray-500">
                No trips assigned yet. Trips will appear here when admin assigns them to you.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  if (isSuperAdmin) {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <CarLoader size="large" message="Loading admin dashboard..." />
        </div>
      );
    }

    const { grandTotals, owners, revenueChart = [], expiringDocs = [] } = adminData;

    // Filter owners by search
    const filteredOwners = owners.filter(owner => {
      if (!adminSearch) return true;
      const q = adminSearch.toLowerCase();
      return (
        owner.name?.toLowerCase().includes(q) ||
        owner.phone?.toLowerCase().includes(q) ||
        owner.email?.toLowerCase().includes(q) ||
        owner.travelsName?.toLowerCase().includes(q)
      );
    });

    // Pagination
    const totalPages = Math.ceil(filteredOwners.length / adminPerPage);
    const paginatedOwners = filteredOwners.slice(
      (adminPage - 1) * adminPerPage,
      adminPage * adminPerPage
    );

    return (
      <div className="space-y-4">
        <PageHeader
          title="Admin Dashboard"
          description="Overview of all owners and their business stats."
        />

        {/* Grand Total Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <StatCard title="Total Owners" value={grandTotals.totalOwners || 0} icon={Users} color="blue" />
          <StatCard title="Total Vehicles" value={grandTotals.totalCars || 0} icon={Car} color="green" />
          <StatCard title="Total Bookings" value={grandTotals.totalBookings || 0} icon={Calendar} color="purple" />
          <StatCard title="Total Revenue" value={formatCurrency(grandTotals.totalRevenue || 0)} icon={IndianRupee} color="yellow" />
        </div>

        {/* Secondary Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/owners')}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">New This Month</p>
              <p className="text-xl font-bold text-blue-600">{grandTotals.newOwnersThisMonth || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Active Owners</p>
              <p className="text-xl font-bold text-green-600">{grandTotals.activeOwners || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Inactive Owners</p>
              <p className="text-xl font-bold text-gray-400">{grandTotals.inactiveOwners || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/owners')}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Pending Verify</p>
              <p className={`text-xl font-bold ${grandTotals.pendingVerification > 0 ? 'text-amber-600' : 'text-green-600'}`}>{grandTotals.pendingVerification || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Verified</p>
              <p className="text-xl font-bold text-green-600">{grandTotals.verified || 0}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/bookings')}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">Pending Bookings</p>
              <p className={`text-xl font-bold ${grandTotals.pendingBookings > 0 ? 'text-amber-600' : 'text-green-600'}`}>{grandTotals.pendingBookings || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Chart + Expiring Docs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Revenue Trend Chart */}
          <Card className="lg:col-span-2">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Platform Revenue (Last 6 Months)</h3>
              {revenueChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={revenueChart}>
                    <defs>
                      <linearGradient id="adminRevGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v/1000)}k`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px' }}
                      formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name === 'revenue' ? 'Revenue' : 'Bookings']}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fill="url(#adminRevGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-10 text-gray-400 text-sm">No revenue data yet</div>
              )}
            </CardContent>
          </Card>

          {/* Expiring Documents & Alerts */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-red-500" />
                Expiring Documents
              </h3>
              {expiringDocs.length > 0 ? (
                <div className="space-y-2 max-h-[220px] overflow-y-auto">
                  {expiringDocs.map((doc, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded-lg text-xs ${doc.isExpired ? 'bg-red-50' : 'bg-amber-50'}`}>
                      <div className="min-w-0 flex-1">
                        <span className={`font-medium ${doc.isExpired ? 'text-red-700' : 'text-amber-700'}`}>
                          {doc.type?.charAt(0).toUpperCase() + doc.type?.slice(1)}
                        </span>
                        <p className="text-gray-500 truncate">{doc.vehicle}</p>
                      </div>
                      <span className={`font-medium flex-shrink-0 ml-2 ${doc.isExpired ? 'text-red-600' : 'text-amber-600'}`}>
                        {doc.isExpired ? 'Expired' : new Date(doc.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">All documents are up to date</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Owners Table */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Owners Overview</h3>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6 px-2"
                onClick={() => navigate('/admin/owners')}
              >
                Manage Owners
              </Button>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone, email or travels..."
                value={adminSearch}
                onChange={(e) => { setAdminSearch(e.target.value); setAdminPage(1); }}
                className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {paginatedOwners.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Owner</TableHead>
                        <TableHead className="text-xs">Travels</TableHead>
                        <TableHead className="text-xs text-center">Status</TableHead>
                        <TableHead className="text-xs text-center">Vehicles</TableHead>
                        <TableHead className="text-xs text-center">Trips</TableHead>
                        <TableHead className="text-xs text-right">Revenue</TableHead>
                        <TableHead className="text-xs text-right">Last Active</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedOwners.map((owner) => (
                        <TableRow key={owner._id} className="hover:bg-gray-50">
                          <TableCell>
                            <div className="text-xs font-medium text-gray-900">{formatName(owner.name)}</div>
                            <div className="text-xs text-gray-500">{owner.phone}</div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs font-medium text-gray-700">{owner.travelsName || '-'}</div>
                            {owner.travelsCity && (
                              <div className="text-xs text-gray-400">{owner.travelsCity}{owner.travelsState ? `, ${owner.travelsState}` : ''}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                              owner.verificationStatus === 'verified' ? 'bg-green-100 text-green-700' :
                              owner.verificationStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {owner.verificationStatus === 'verified' ? 'Verified' :
                               owner.verificationStatus === 'rejected' ? 'Rejected' : 'Pending'}
                            </span>
                            {owner.isActive && (
                              <span className="block text-[9px] text-green-500 mt-0.5">Active</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-center font-medium">{owner.totalCars}</TableCell>
                          <TableCell className="text-xs text-center font-medium">{owner.completedBookings}</TableCell>
                          <TableCell className="text-xs text-right font-bold text-green-600">
                            {formatCurrency(owner.totalRevenue)}
                          </TableCell>
                          <TableCell className="text-xs text-right text-gray-400">
                            {owner.lastActive ? new Date(owner.lastActive).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Showing {(adminPage - 1) * adminPerPage + 1}-{Math.min(adminPage * adminPerPage, filteredOwners.length)} of {filteredOwners.length} owners
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setAdminPage(p => Math.max(1, p - 1))}
                        disabled={adminPage === 1}
                        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setAdminPage(page)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                            adminPage === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setAdminPage(p => Math.min(totalPages, p + 1))}
                        disabled={adminPage === totalPages}
                        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6 text-xs text-gray-500">
                {adminSearch ? 'No owners match your search.' : 'No owners registered yet.'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { icon: Users, label: 'Verify Owners', desc: 'Approve pending', path: '/admin/owners', color: 'bg-blue-500' },
            { icon: Car, label: 'All Vehicles', desc: 'View all cars', path: '/cars', color: 'bg-green-500' },
            { icon: Calendar, label: 'All Bookings', desc: 'View bookings', path: '/bookings', color: 'bg-purple-500' },
            { icon: TrendingUp, label: 'Reports', desc: 'Analytics', path: '/reports', color: 'bg-orange-500' },
          ].map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all group"
            >
              <div className={`p-2 rounded-lg ${action.color} text-white group-hover:scale-105 transition-transform`}>
                <action.icon className="h-4 w-4" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">{action.label}</p>
                <p className="text-[10px] text-gray-400">{action.desc}</p>
              </div>
            </button>
          ))}
          <button
            onClick={() => { setNotifyModal(true); setNotifyResult(null); }}
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all group"
          >
            <div className="p-2 rounded-lg bg-indigo-500 text-white group-hover:scale-105 transition-transform">
              <AlertCircle className="h-4 w-4" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">Notify Owners</p>
              <p className="text-[10px] text-gray-400">Send email</p>
            </div>
          </button>
        </div>

        {/* Notify Owners Modal */}
        {notifyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-5 text-white">
                <h3 className="text-lg font-bold">Send Notification to Owners</h3>
                <p className="text-indigo-100 text-sm mt-1">This will send an email to selected owners</p>
              </div>
              <div className="p-6 space-y-4">
                {/* Recipients */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recipients</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setNotifyForm(f => ({ ...f, sendToAll: true, ownerIds: [] }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        notifyForm.sendToAll ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      All Owners ({grandTotals.totalOwners || 0})
                    </button>
                    <button
                      type="button"
                      onClick={() => setNotifyForm(f => ({ ...f, sendToAll: false }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        !notifyForm.sendToAll ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Select Owners
                    </button>
                  </div>
                </div>

                {/* Owner selection */}
                {!notifyForm.sendToAll && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Owners ({notifyForm.ownerIds.length} selected)
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                      {owners.map(owner => (
                        <label key={owner._id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifyForm.ownerIds.includes(owner._id)}
                            onChange={(e) => {
                              setNotifyForm(f => ({
                                ...f,
                                ownerIds: e.target.checked
                                  ? [...f.ownerIds, owner._id]
                                  : f.ownerIds.filter(id => id !== owner._id)
                              }));
                            }}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-700">{formatName(owner.name)}</span>
                          <span className="text-xs text-gray-400">{owner.travelsName || owner.email}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={notifyForm.subject}
                    onChange={(e) => setNotifyForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="e.g., Important Update, New Feature..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={notifyForm.message}
                    onChange={(e) => setNotifyForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Write your message to owners..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Result */}
                {notifyResult && (
                  <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                    notifyResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {notifyResult.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    {notifyResult.text}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setNotifyModal(false)}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendNotification}
                    disabled={notifySending || !notifyForm.subject.trim() || !notifyForm.message.trim() || (!notifyForm.sendToAll && notifyForm.ownerIds.length === 0)}
                    className="flex-1 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-xl transition-colors"
                  >
                    {notifySending ? 'Sending...' : `Send to ${notifyForm.sendToAll ? 'All Owners' : `${notifyForm.ownerIds.length} Owner(s)`}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Verification Status Banner Component
  const VerificationBanner = () => {
    if (user?.role !== 'owner') return null;

    const status = user?.verificationStatus;
    if (status === 'verified') return null;

    const bannerConfig = {
      pending: {
        bg: 'bg-yellow-50 border-yellow-200',
        icon: Clock,
        iconColor: 'text-yellow-600',
        title: 'Account Verification Pending',
        message: 'Your account is awaiting admin verification. You cannot add vehicles until your account is verified.',
        textColor: 'text-yellow-800'
      },
      rejected: {
        bg: 'bg-red-50 border-red-200',
        icon: XCircle,
        iconColor: 'text-red-600',
        title: 'Account Verification Rejected',
        message: 'Your account verification was rejected. Please contact support for more information.',
        textColor: 'text-red-800'
      },
      suspended: {
        bg: 'bg-gray-50 border-gray-300',
        icon: ShieldAlert,
        iconColor: 'text-gray-600',
        title: 'Account Suspended',
        message: 'Your account has been suspended. You cannot add or manage vehicles. Please contact support.',
        textColor: 'text-gray-800'
      }
    };

    const config = bannerConfig[status] || bannerConfig.pending;
    const Icon = config.icon;

    return (
      <div className={`mb-4 p-4 rounded-lg border ${config.bg}`}>
        <div className="flex items-start">
          <Icon className={`h-5 w-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
          <div className="ml-3">
            <h3 className={`text-sm font-medium ${config.textColor}`}>{config.title}</h3>
            <p className={`text-sm ${config.textColor} mt-1 opacity-80`}>{config.message}</p>
          </div>
        </div>
      </div>
    );
  };

  // Admin/Owner Dashboard

  // Show full-page loader while loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <CarLoader size="large" message="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Welcome back! Here's an overview of your vehicle booking system."
      />

      {/* Verification Status Banner (for owners) */}
      <VerificationBanner />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
        <StatCard
          title="Total Vehicles"
          value={stats.totalCars}
          icon={Car}
          trend={trends.cars?.direction}
          trendValue={trends.cars?.value}
          color="blue"
        />
        <StatCard
          title="Active Bookings"
          value={stats.activeBookings}
          icon={Calendar}
          trend={trends.bookings?.direction}
          trendValue={trends.bookings?.value}
          color="green"
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          icon={IndianRupee}
          trend={trends.revenue?.direction}
          trendValue={trends.revenue?.value}
          color="purple"
        />
        <StatCard
          title="Active Journeys"
          value={stats.activeJourneys}
          icon={Route}
          trend={trends.journeys?.direction}
          trendValue={trends.journeys?.value}
          color="yellow"
        />
      </div>

      {/* Dashboard Alerts Section */}
      {dashboardAlerts && isAdminLevel && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {/* Pending Approvals */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/bookings')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Pending Approvals</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">{dashboardAlerts.pendingBookings}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              {dashboardAlerts.pendingBookings > 0 && (
                <p className="text-xs text-amber-600 mt-2 font-medium">Tap to review</p>
              )}
            </CardContent>
          </Card>

          {/* Vehicle Status */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Vehicle Status</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-lg font-bold text-green-600">{dashboardAlerts.vehicleStatus.available} <span className="text-xs font-normal text-gray-400">free</span></span>
                    <span className="text-lg font-bold text-blue-600">{dashboardAlerts.vehicleStatus.onTrip} <span className="text-xs font-normal text-gray-400">on trip</span></span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Car className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${dashboardAlerts.vehicleStatus.total > 0 ? (dashboardAlerts.vehicleStatus.onTrip / dashboardAlerts.vehicleStatus.total * 100) : 0}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Today's Earnings */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Today's Earnings</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(dashboardAlerts.todayEarnings.amount)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <IndianRupee className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">{dashboardAlerts.todayEarnings.count} payment{dashboardAlerts.todayEarnings.count !== 1 ? 's' : ''} today</p>
            </CardContent>
          </Card>

          {/* Alerts Count */}
          <Card className={dashboardAlerts.expiringDocuments.length > 0 || dashboardAlerts.expiringLicenses.length > 0 ? 'border-red-200 bg-red-50/50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Alerts</p>
                  <p className={`text-2xl font-bold mt-1 ${(dashboardAlerts.expiringDocuments.length + dashboardAlerts.expiringLicenses.length) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {dashboardAlerts.expiringDocuments.length + dashboardAlerts.expiringLicenses.length}
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${(dashboardAlerts.expiringDocuments.length + dashboardAlerts.expiringLicenses.length) > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <AlertCircle className={`h-5 w-5 ${(dashboardAlerts.expiringDocuments.length + dashboardAlerts.expiringLicenses.length) > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {dashboardAlerts.expiringDocuments.length > 0 ? `${dashboardAlerts.expiringDocuments.length} doc expiring` : 'All documents OK'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expiring Documents & Pending Payments Detail */}
      {dashboardAlerts && isAdminLevel && (dashboardAlerts.expiringDocuments.length > 0 || dashboardAlerts.pendingPayments.length > 0 || dashboardAlerts.recentPayments.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Expiring Documents */}
          {(dashboardAlerts.expiringDocuments.length > 0 || dashboardAlerts.expiringLicenses.length > 0) && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-red-500" />
                  Expiring Documents & Licenses
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dashboardAlerts.expiringDocuments.map((doc, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded-lg text-xs ${doc.isExpired ? 'bg-red-50' : 'bg-amber-50'}`}>
                      <div>
                        <span className={`font-medium ${doc.isExpired ? 'text-red-700' : 'text-amber-700'}`}>
                          {doc.type?.charAt(0).toUpperCase() + doc.type?.slice(1)}
                        </span>
                        <span className="text-gray-500 ml-2">{doc.vehicle}</span>
                      </div>
                      <span className={`font-medium ${doc.isExpired ? 'text-red-600' : 'text-amber-600'}`}>
                        {doc.isExpired ? 'Expired' : new Date(doc.expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                  ))}
                  {dashboardAlerts.expiringLicenses.map((lic, i) => (
                    <div key={`lic-${i}`} className={`flex items-center justify-between p-2 rounded-lg text-xs ${lic.isExpired ? 'bg-red-50' : 'bg-amber-50'}`}>
                      <div>
                        <span className={`font-medium ${lic.isExpired ? 'text-red-700' : 'text-amber-700'}`}>License</span>
                        <span className="text-gray-500 ml-2">{lic.name}</span>
                      </div>
                      <span className={`font-medium ${lic.isExpired ? 'text-red-600' : 'text-amber-600'}`}>
                        {lic.isExpired ? 'Expired' : new Date(lic.licenseExpiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Payments */}
          {dashboardAlerts.pendingPayments.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <IndianRupee className="h-4 w-4 text-amber-500" />
                  Pending Payments
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dashboardAlerts.pendingPayments.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg text-xs cursor-pointer hover:bg-amber-100" onClick={() => navigate('/bookings')}>
                      <div>
                        <span className="font-medium text-gray-900">{formatName(p.customerName)}</span>
                        <span className="text-gray-500 ml-2">{formatVehicleName({ make: p.vehicle?.split(' ')[0], model: p.vehicle?.split(' ').slice(1).join(' ') })}</span>
                      </div>
                      <span className="font-bold text-red-600">{formatCurrency(p.due)} due</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      )}

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            {/* Chart Header */}
            <div className="flex flex-col gap-3 mb-4">
              {/* Title Row */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Revenue Overview</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl font-bold text-gray-900">{formatCurrency(totalRevenueChart)}</span>
                    <span className={`flex items-center text-xs font-medium px-1.5 py-0.5 rounded ${
                      growthPercentage >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {growthPercentage >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {Math.abs(growthPercentage)}%
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowComparison(!showComparison)}
                    className={`p-1.5 rounded-lg transition-all ${
                      showComparison ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Compare with previous period"
                  >
                    <TrendingUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => fetchDashboardData()}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                    title="Refresh data"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                    title="Download report"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Controls Row */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Period Filter */}
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  {[
                    { value: 'daily', label: 'Day' },
                    { value: 'weekly', label: 'Week' },
                    { value: 'monthly', label: 'Month' },
                    { value: 'yearly', label: 'Year' }
                  ].map((period) => (
                    <button
                      key={period.value}
                      onClick={() => setChartPeriod(period.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        chartPeriod === period.value
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>

                {/* Chart Type Filter */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setChartType('area')}
                    className={`p-1.5 rounded-md transition-all ${
                      chartType === 'area' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="Area Chart"
                  >
                    <AreaChartIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setChartType('line')}
                    className={`p-1.5 rounded-md transition-all ${
                      chartType === 'line' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="Line Chart"
                  >
                    <LineChartIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setChartType('bar')}
                    className={`p-1.5 rounded-md transition-all ${
                      chartType === 'bar' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="Bar Chart"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Legend */}
            {showComparison && (
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--primary-color)' }}></div>
                  <span className="text-gray-600">Current Period</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span className="text-gray-600">Previous Period</span>
                </div>
              </div>
            )}

            {/* Chart */}
            <ResponsiveContainer width="100%" height={220}>
              {chartType === 'line' ? (
                <LineChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary-color)" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="var(--primary-color)" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(value) => `₹${(value/1000)}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [formatCurrency(value), name === 'revenue' ? 'Current' : 'Previous']}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                  />
                  {showComparison && (
                    <Line
                      type="monotone"
                      dataKey="previous"
                      stroke="#d1d5db"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="var(--primary-color)"
                    strokeWidth={2.5}
                    dot={{ fill: '#fff', stroke: 'var(--primary-color)', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: 'var(--primary-color)', strokeWidth: 2, fill: '#fff' }}
                  />
                </LineChart>
              ) : chartType === 'bar' ? (
                <BarChart data={revenueData} barGap={showComparison ? 2 : 0}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(value) => `₹${(value/1000)}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [formatCurrency(value), name === 'revenue' ? 'Current' : 'Previous']}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                  />
                  {showComparison && (
                    <Bar
                      dataKey="previous"
                      fill="#e5e7eb"
                      radius={[4, 4, 0, 0]}
                    />
                  )}
                  <Bar
                    dataKey="revenue"
                    fill="var(--primary-color)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              ) : (
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary-color)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--primary-color)" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorAreaPrev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#9ca3af" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(value) => `₹${(value/1000)}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [formatCurrency(value), name === 'revenue' ? 'Current' : 'Previous']}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                  />
                  {showComparison && (
                    <Area
                      type="monotone"
                      dataKey="previous"
                      stroke="#d1d5db"
                      fill="url(#colorAreaPrev)"
                      strokeWidth={1.5}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="var(--primary-color)"
                    fill="url(#colorArea)"
                    strokeWidth={2}
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-gray-100">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Total</p>
                <p className="text-sm font-bold text-gray-900">{formatCurrency(totalRevenueChart)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Average</p>
                <p className="text-sm font-bold text-gray-900">
                  {formatCurrency(revenueData.length > 0 ? Math.round(totalRevenueChart / revenueData.length) : 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Highest</p>
                <p className="text-sm font-bold text-green-600">
                  {formatCurrency(revenueData.length > 0 ? Math.max(...revenueData.map(item => item.revenue)) : 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Lowest</p>
                <p className="text-sm font-bold text-red-500">
                  {formatCurrency(revenueData.length > 0 ? Math.min(...revenueData.map(item => item.revenue)) : 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments — right side of chart */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Recent Payments
            </h3>
            {dashboardAlerts?.recentPayments?.length > 0 ? (
              <div className="space-y-2 max-h-[280px] overflow-y-auto">
                {dashboardAlerts.recentPayments.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-green-50 rounded-lg text-xs">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{formatName(p.customerName)}</p>
                      <p className="text-gray-400 text-[10px]">{p.mode} {p.vehicleName ? `• ${p.vehicleName}` : ''}</p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="font-bold text-green-600">{formatCurrency(p.amount)}</p>
                      <p className="text-gray-400 text-[10px]">{new Date(p.recordedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <IndianRupee className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No recent payments</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {[
          { icon: Calendar, label: 'New Booking', desc: 'Create a booking', path: '/bookings/create', color: 'bg-blue-500' },
          { icon: Car, label: 'Add Vehicle', desc: 'Register new car', path: '/cars/add', color: 'bg-green-500' },
          { icon: Route, label: 'Track Journey', desc: 'Monitor trips', path: '/journeys', color: 'bg-purple-500' },
          { icon: TrendingUp, label: 'View Reports', desc: 'Analytics & insights', path: '/reports', color: 'bg-orange-500' },
          { icon: Users, label: 'Drivers', desc: 'Manage drivers', path: '/drivers', color: 'bg-indigo-500' },
          { icon: Building2, label: 'Settings', desc: 'App settings', path: '/settings', color: 'bg-gray-600' },
        ].map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.path)}
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all group"
          >
            <div className={`p-2 rounded-lg ${action.color} text-white group-hover:scale-105 transition-transform`}>
              <action.icon className="h-4 w-4" />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">{action.label}</p>
              <p className="text-[10px] text-gray-400">{action.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Recent Bookings */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-700">Recent Bookings</h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6 px-2"
              onClick={() => navigate('/bookings')}
            >
              View All
            </Button>
          </div>
          {loading ? (
            <CarLoader size="small" message="Loading data..." />
          ) : recentBookings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Booking ID</TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs">Vehicle</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentBookings.map((booking) => (
                  <TableRow key={booking._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/bookings?id=${booking._id}`)}>
                    <TableCell className="text-xs font-medium text-blue-600">
                      #{booking._id.slice(-6)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {formatName(booking.user?.name || booking.customerName) || 'N/A'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {formatVehicleName(booking.carId, { showYear: false })}
                    </TableCell>
                    <TableCell className="text-xs">{formatDateTime(booking.startDate)}</TableCell>
                    <TableCell>{getStatusBadge(booking.status)}</TableCell>
                    <TableCell className="text-xs font-semibold">
                      {formatCurrency(booking.totalCost || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-4 text-xs text-gray-500">
              No bookings found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share public travels page (owners & their staff) — bottom of dashboard */}
      {(user?.role === 'owner' ? user?._id : user?.ownerId) && (
        <ShareTravelsCard
          ownerId={user?.role === 'owner' ? user?._id : user?.ownerId}
          travelsName={user?.travelsInfo?.name}
        />
      )}
    </div>
  );
};

export default Dashboard;
